import React, { useState, useEffect, useMemo } from 'react'
import { Select, Typography, Form, TreeSelect } from 'antd'
import { PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons'
import cloneDeep from 'lodash/cloneDeep'
import difference from 'lodash/difference'
import isEmpty from 'lodash/isEmpty'
import uniqBy from 'lodash/uniqBy'
import isEqual from 'lodash/isEqual'

import IntlMessages from '../utils/IntlMessages'

const { Option } = Select
const { Text } = Typography

import { IFilter } from '../../types/filter'
import { IFilterAdvanced, ISelected, IFilterSelect } from './types'
import { useIntl } from 'react-intl'

const FilterPagesMap = {
  dashboard: 'dashboardFilters',
  wallchart: 'wallchartFilters',
  users: 'usersFilters',
  calendar: 'calendarFilters',
  msTeamsDashboard: 'msTeamsDashboardFilters',
  msTeamsCalendar: 'msTeamsCalendarFilters',
  notificationForm: 'notificationFormFilters',
  calendarSync: 'calendarSyncFilters',
  automationsForm: 'automationsFormFilters',
}

// Helper to get localStorage key for a page
const getStorageKey = (page: string | undefined, currentUserId: string | undefined): string | null => {
  if (!page) return null
  const storageKeyBase = FilterPagesMap[page as keyof typeof FilterPagesMap]
  if (!storageKeyBase) return null
  return `${currentUserId ? `${currentUserId}#` : ''}${storageKeyBase}`
}

const Filter = ({
  availableFilters,
  showAddButton,
  data,
  selected,
  onSelect,
  onAdd,
  onRemove,
  lockTypeSelector,
  isForm = false,
  filterByWidth,
}: IFilterSelect) : React.ReactElement => {

  const [showTreeDataForDepartment, setShowTreeDataForDepartment] = useState(false)

  useEffect(() => {
    let hasNested = false
    if (selected.type === 'Departments' && data['Departments'] && data['Departments'].length > 0) {
      for (const department of data['Departments']) {
        if (department.parentId != null) {
          hasNested = true
          break
        }
      }
    }
    setShowTreeDataForDepartment(hasNested)
  }, [data, selected])

  const { formatMessage } = useIntl()

  const onSelectValues = (value) => {
    if (value) {
      onSelect(selected.type, value)
    } else {
      onSelect(selected.type)
    }
  }

  const getSelectionValue = (type: string) => {
    return type ? formatMessage({id: `app.${type.toLowerCase()}`}) : ''
  }

  return (
    <div id='parent-select' style={{paddingBottom: 3, position: 'relative', display: 'flex'}}>
      <Select
        style={
          isForm
            ? {width: filterByWidth}
            : {width: filterByWidth, marginLeft: 5}
        }
        placeholder={<IntlMessages id='app.select' />}
        onSelect={(type) => onSelect(type)}
        onChange={(value) => onSelectValues(value)}
        value={selected.type || undefined}
        disabled={lockTypeSelector}
        notFoundContent=''
        getPopupContainer={() =>
          document.getElementById('parent-select') as HTMLElement
        }
      >
        {availableFilters.map((item) => (
          <Option key={item} value={item}>
            <IntlMessages id={`app.${item.toLowerCase()}`} />
          </Option>
        ))}
      </Select>
      {showTreeDataForDepartment ? (
        <TreeSelect
          disabled={!selected.type}
          value={selected.values}
          style={
            isForm
              ? {width: `calc(100% - ${filterByWidth + 50}px)`, marginLeft: 5}
              : {width: 195, marginLeft: 5}
          }
          onChange={onSelectValues}
          multiple
          treeNodeFilterProp='name'
          getPopupContainer={() =>
            document.getElementById('parent-select') as HTMLElement
          }
          showCheckedStrategy={TreeSelect.SHOW_ALL}
          treeData={data['Departments']}
          treeDataSimpleMode={{id: 'id', pId: 'parentId'}}
          fieldNames={{label: 'name', value: 'id'}}
        />
      ) : (
        <Select
          disabled={!selected.type}
          mode='multiple'
          onChange={onSelectValues}
          value={selected.values}
          style={
            isForm
              ? {width: `calc(100% - ${filterByWidth + 50}px)`, marginLeft: 5}
              : {width: 195, marginLeft: 5}
          }
          optionFilterProp='label'
          getPopupContainer={() =>
            document.getElementById('parent-select') as HTMLElement
          }
        >
          {selected.type &&
            data[selected.type] &&
            data[selected.type]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((item) => (
                <Option key={item.id} label={item.name} value={item.id}>
                  {item.name}
                </Option>
              ))}
        </Select>
      )}
      {selected.type && (
        <MinusCircleOutlined
          onClick={onRemove}
          style={{color: 'red', marginLeft: 5}}
        />
      )}
      {showAddButton &&
        selected.type &&
        selected.values &&
        selected.values.length > 0 && (
        <PlusCircleOutlined
          onClick={onAdd}
          style={{color: 'green', marginLeft: 5}}
        />
      )}
    </div>
  )
}

// Helper: build filtered data consistent with availableFilters computation
const buildFilteredData = (
  dataSource: Record<string, any>,
  showLabelsFlag: boolean,
  showLeaveTypesFlag: boolean
) => {
  const filtered = { ...dataSource }
  if (!showLabelsFlag) {
    delete filtered.Labels
  }
  if (!showLeaveTypesFlag) {
    delete filtered['Leave Types']
  }
  return filtered
}

// Helper: sanitize selected values, removing invalid IDs
// preserveEmptyPlaceholders: if true, keeps empty {} entries (needed for "add filter" UI)
const sanitizeSelectedValues = (
  selectedItems: Array<ISelected | {}>,
  availableFilterTypes: string[],
  validIdsByType: Record<string, Set<string>>,
  preserveEmptyPlaceholders = false
): Array<ISelected | {}> => {
  const sanitized: Array<ISelected | {}> = []
  let hasEmptyPlaceholder = false
  for (const entry of selectedItems) {
    if (isEmpty(entry)) {
      hasEmptyPlaceholder = true
      continue
    }
    const item = entry as ISelected
    if (!item.type || !availableFilterTypes.includes(item.type)) continue
    // If values is undefined/empty object, user is selecting - keep as is
    if (item.values === undefined || (typeof item.values === 'object' && !Array.isArray(item.values) && isEmpty(item.values))) {
      sanitized.push({ type: item.type, values: undefined } as ISelected)
      continue
    }
    // Filter out invalid IDs
    const validValues = Array.isArray(item.values)
      ? item.values.filter(v => validIdsByType[item.type]?.has(v))
      : []
    if (validValues.length > 0) {
      sanitized.push({ type: item.type, values: validValues })
    }
  }
  // Preserve the last empty placeholder if requested and there was one
  if (preserveEmptyPlaceholders && hasEmptyPlaceholder) {
    sanitized.push({})
  }
  return sanitized
}

const FilterAdvanced = ({
  data,
  onChangeFilter,
  page,
  showLabels = false,
  showLeaveTypes = false,
  isForm = false,
  toolTipInfo = '',
  initialValues = [{}],
  saveFilters = true,
  filterByWidth = 125,
  shouldReset,
  currentUserId,
}: IFilterAdvanced): React.ReactElement => {
  const [ selectedTypes, setSelectedTypes ] = useState<string[]>([])
  const [ selected, setSelected ] = useState<ISelected[] | {}[]>(initialValues)
  const [ availableFilters, setAvailableFilters ] = useState(Object.keys(data))

  useEffect(() => {
    const storageKey = getStorageKey(page, currentUserId)
    if (storageKey && saveFilters && localStorage.getItem(storageKey) && isEmpty(initialValues[0])) {
      const newSelected: ISelected[] | {}[] = JSON.parse(localStorage.getItem(storageKey) || '')
      setSelected(newSelected)
      setSelectedTypes(newSelected.map(f => f.type))
    } else {
      setSelected(initialValues)
      if (storageKey && saveFilters) {
        localStorage.setItem(storageKey, JSON.stringify(initialValues))
      }
      setSelectedTypes(() => {
        if(initialValues && !isEmpty(initialValues[0])) {
          return initialValues.map(f => f.type)
        } else {
          return []
        }
      })
    }
  }, [])

  useEffect(() => {
    if (shouldReset) {
      setSelected(initialValues)
      setSelectedTypes(() => {
        if(initialValues && !isEmpty(initialValues[0])) {
          return initialValues.map(f => f.type)
        } else {
          return []
        }
      })
      const storageKey = getStorageKey(page, currentUserId)
      if (storageKey && saveFilters) {
        localStorage.setItem(storageKey, JSON.stringify([{}]))
      }
    }
  }, [shouldReset])

  // Memoize filtered data and valid ids map
  const filteredDataMemo = useMemo(() => buildFilteredData(data as Record<string, any>, showLabels, showLeaveTypes), [data, showLabels, showLeaveTypes])
  const validIdsByType = useMemo(() => {
    return Object.keys(filteredDataMemo).reduce((acc, key) => {
      const items = (filteredDataMemo as any)[key] || []
      acc[key] = new Set((items as Array<{ id: string }>).map(item => item.id))
      return acc
    }, {} as Record<string, Set<string>>)
  }, [filteredDataMemo])

  useEffect(() => {
    setAvailableFilters(Object.keys(filteredDataMemo))
  }, [filteredDataMemo])

  // Sanitize selected values when data changes (for both forms and non-forms)
  // This removes invalid IDs (e.g., deleted labels) from the selection
  useEffect(() => {
    // Only run when we have actual data loaded
    const hasData = Object.keys(validIdsByType).some(key => validIdsByType[key].size > 0)
    if (!hasData) return

    const currentFilters = Object.keys(filteredDataMemo)
    // Preserve empty placeholders for forms (user might be adding a new filter)
    const sanitized = sanitizeSelectedValues(selected as Array<ISelected | {}>, currentFilters, validIdsByType, isForm)
    const nextSelected = sanitized.length > 0 ? sanitized : [{}]

    if (!isEqual(nextSelected, selected)) {
      setSelected(nextSelected)
      const nextTypes = (sanitized as ISelected[]).filter(f => f.type).map(f => f.type)
      setSelectedTypes(nextTypes)

      // For non-forms, also save to localStorage
      if (!isForm) {
        const storageKey = getStorageKey(page, currentUserId)
        if (storageKey && saveFilters) {
          localStorage.setItem(storageKey, JSON.stringify(nextSelected))
        }
      }
    }
  }, [validIdsByType, filteredDataMemo, isForm])

  const onSelect = (type: string, values) => {
    const storageKey = getStorageKey(page, currentUserId)
    if (selectedTypes.includes(type)) {
      if(availableFilters.includes(values)) {
        setSelected(prevState => {
          const newStateSelected = prevState.filter(state => state && (state as ISelected).type !== type)
          return [...newStateSelected, { type: values, values: undefined }]
        })
        setSelectedTypes(prevState => {
          const newStateSelectedTypes = prevState.filter(state => state !== type)
          return [...newStateSelectedTypes, values]
        })
      } else {
        const newSelected = selected
          .map(item => (item.type === type) ? { type, values } : item)
          .filter(f => !isEmpty(f))
        setSelected(newSelected)
        if (storageKey && saveFilters) {
          localStorage.setItem(storageKey, JSON.stringify(newSelected))
        }
      }
    } else if (type) {
      setSelected((prevState) => {
        const newSelected = uniqBy([...prevState.filter(f => !isEmpty(f)), { type, values }], 'type')
          .map(item => {
            if (isEmpty((item as ISelected).values)) {
              return {
                type: (item as ISelected).type,
                values: undefined,
              }
            }
            return item
          })

        if (storageKey && saveFilters) {
          localStorage.setItem(storageKey, JSON.stringify(newSelected))
        }
        return newSelected
      })
      setSelectedTypes((prevState) => {
        return [...new Set([...prevState, type])]
      })
    }
  }

  useEffect(() => {
    const filter: IFilter = {
      locationIds: [],
      teamIds: [],
      labelIds: [],
    }

    // Only include leaveTypeIds if showLeaveTypes is enabled
    if (showLeaveTypes) {
      filter.leaveTypeIds = []
    }

    selected.map(item => {
      if (item?.type === 'Locations') {
        filter.locationIds = item.values || []
      }
      if (item?.type === 'Departments') {
        filter.teamIds = item.values || []
      }
      if (item?.type === 'Labels') {
        filter.labelIds = item.values || []
      }
      if (item?.type === 'Leave Types' && showLeaveTypes) {
        filter.leaveTypeIds = item.values || []
      }
    })
    onChangeFilter(filter)
  }, [selected, showLeaveTypes])

  const onAdd = () => {
    const newSelected = [
      ...selected,
      {},
    ]
    setSelected(newSelected)
    const storageKey = getStorageKey(page, currentUserId)
    if (storageKey && saveFilters) {
      localStorage.setItem(storageKey, JSON.stringify(newSelected))
    }
  }

  const onRemove = (index: number) => {
    const newSelected = cloneDeep(selected)
    if(!isEmpty(newSelected)) {
      const removed = newSelected.splice(index, 1) as ISelected[]
      setSelected(newSelected.length ? newSelected : [{}])
      setSelectedTypes(selectedTypes.filter(type => type !== removed[0].type))
      const storageKey = getStorageKey(page, currentUserId)
      if (storageKey && saveFilters) {
        localStorage.setItem(storageKey, JSON.stringify(newSelected.length ? newSelected : [{}]))
      }
    }
  }

  return (
    <div className="vt-filters-wrapper">
      {/* This condition is for displaying real data instead of IDs (for slow internet) */}
      {data.Locations && data.Locations.length > 0 &&
        <>
          {isForm ?
            <Form.Item
              label={<IntlMessages id="components.filterSimple.filterBy" />}
              // name="filter"
              tooltip={toolTipInfo && <IntlMessages id={toolTipInfo} />}
            >
              {selected.map((filter, i: number) => (
                <Filter
                  isForm={true}
                  availableFilters={difference(availableFilters, selectedTypes)}
                  showAddButton={
                    i !== (availableFilters.length - 1) &&
                    i === (selected.length - 1)
                  }
                  lockTypeSelector={
                    i < (selected.length - 1)
                  }
                  data={data}
                  selected={selected[i] as ISelected}
                  onSelect={onSelect}
                  onAdd={onAdd}
                  onRemove={() => onRemove(i)}
                  key={i}
                  filterByWidth={filterByWidth}
                />
              ))}
            </Form.Item> :
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Text style={{ paddingTop: 5 }}>
                <IntlMessages id="components.filterSimple.filterBy" />
              </Text>
              <div>
                {selected.map((filter, i: number) => (
                  <Filter
                    availableFilters={difference(availableFilters, selectedTypes)}
                    showAddButton={
                      i !== (availableFilters.length - 1) &&
                      i === (selected.length - 1)
                    }
                    lockTypeSelector={
                      i < (selected.length - 1)
                    }
                    data={data}
                    selected={selected[i] as ISelected}
                    onSelect={onSelect}
                    onAdd={onAdd}
                    onRemove={() => onRemove(i)}
                    key={i}
                    filterByWidth={filterByWidth}
                  />
                ))}
              </div>
            </div>
          }
        </>
      }
    </div>
  )
}

export default React.memo(FilterAdvanced)